import type { CueItemPhase, FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import type { FlowControlSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { cueScopeMatchesSegmentKind, isFlowControlInBetweenSegmentKind } from '@/helpers/simulation-cue-engine.helper';
import { parseCueControlItemKey } from '@/helpers/perform-staging.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  isInBetweenPlayoutPlayingInCueState,
  resolveInBetweenPlayoutFromCueState,
} from '@/helpers/flow-control-in-between-playout.helper';

export type SlotSegmentAnchor = 'flow_start' | 'flow_end' | 'slot';

const AUTO_ONLY_SEGMENT_KINDS = new Set<FlowControlSegmentKind>(['pre_flow', 'after_flow']);

function resolveSlotId(slotId?: string): string | undefined {
  if (!slotId) return undefined;
  return getObjectId(slotId) ?? slotId;
}

function isInBetweenPlayoutActiveOnSlot(cueState: FlowCueStateV1, resolvedSlotId: string | undefined): boolean {
  if (!resolvedSlotId) {
    return false;
  }
  const inBetween = resolveInBetweenPlayoutFromCueState(cueState);
  if (!inBetween) {
    return false;
  }
  const slotId = resolveSlotId(inBetween.slotId);
  if (!slotId || slotId !== resolvedSlotId) {
    return false;
  }
  return isInBetweenPlayoutPlayingInCueState(cueState, inBetween.segmentKind, inBetween.slotId);
}

function slotIdFromCueSegmentAnchor(anchor: string | undefined): string | undefined {
  if (!anchor?.startsWith('slot:')) {
    return undefined;
  }
  return resolveSlotId(anchor.slice('slot:'.length));
}

function slotIdsMatch(left: string | undefined, right: string | undefined): boolean {
  const normalizedLeft = resolveSlotId(left);
  const normalizedRight = resolveSlotId(right);
  if (!normalizedRight) {
    return true;
  }
  if (!normalizedLeft) {
    return false;
  }
  return normalizedLeft === normalizedRight;
}

function isOperatorSegmentKindActive(
  cueState: FlowCueStateV1,
  segmentKind: FlowControlSegmentKind,
  segmentAnchor: SlotSegmentAnchor,
  resolvedSlotId: string | undefined,
): boolean {
  const step = cueState.currentStep;
  if (cueScopeMatchesSegmentKind(step?.scope, segmentKind)) {
    if (segmentAnchor === 'slot' && resolvedSlotId) {
      const stepSlotId = slotIdFromCueSegmentAnchor(step?.segmentAnchor) ?? resolveSlotId(cueState.activeSlotId);
      if (slotIdsMatch(stepSlotId, resolvedSlotId)) {
        return true;
      }
    } else if (segmentAnchor !== 'slot') {
      return true;
    }
  }

  // currentStep is authoritative — stale activeSegmentKind must not keep a prior segment lit.
  if (step?.scope) {
    if (!cueScopeMatchesSegmentKind(step.scope, segmentKind)) {
      return false;
    }
    if (segmentAnchor === 'slot' && resolvedSlotId) {
      const stepSlotId = slotIdFromCueSegmentAnchor(step?.segmentAnchor) ?? resolveSlotId(cueState.activeSlotId);
      if (!slotIdsMatch(stepSlotId, resolvedSlotId)) {
        return false;
      }
    }
  }

  if (cueState.activeSegmentKind !== segmentKind) {
    return false;
  }
  if (segmentAnchor === 'slot' && resolvedSlotId) {
    if (!slotIdsMatch(cueState.activeSlotId, resolvedSlotId)) {
      return false;
    }
  }
  return cueState.isSegmentRunning === true;
}

function isAutoOnlySegmentKindActive(cueState: FlowCueStateV1, segmentKind: FlowControlSegmentKind): boolean {
  if (cueState.currentStep?.scope === segmentKind) {
    return true;
  }
  const hasOnAirInScope = Object.values(cueState.items ?? {}).some(
    (item) => item.scope === segmentKind && item.phase === 'on_air' && item.isCurrent !== false,
  );
  if (hasOnAirInScope) {
    return true;
  }
  if (cueState.activeSegmentKind !== segmentKind) {
    return false;
  }
  return Object.values(cueState.items ?? {}).some((item) => item.scope === segmentKind && item.phase === 'on_air');
}

/** Whether a Control tab segment row should show stop (live / auto-cue play mode). */
export function isLiveSegmentKindActive(
  cueState: FlowCueStateV1 | undefined,
  segmentKind: FlowControlSegmentKind | undefined,
  segmentAnchor: SlotSegmentAnchor,
  resolvedSlotId: string | undefined,
): boolean {
  if (!cueState || !segmentKind) {
    return false;
  }

  if (isFlowControlInBetweenSegmentKind(segmentKind) && resolvedSlotId) {
    return isInBetweenPlayoutPlayingInCueState(cueState, segmentKind, resolvedSlotId);
  }

  if (
    (segmentKind === 'session' || segmentKind === 'break') &&
    isInBetweenPlayoutActiveOnSlot(cueState, resolvedSlotId)
  ) {
    return false;
  }

  if (AUTO_ONLY_SEGMENT_KINDS.has(segmentKind)) {
    return isAutoOnlySegmentKindActive(cueState, segmentKind);
  }

  return isOperatorSegmentKindActive(cueState, segmentKind, segmentAnchor, resolvedSlotId);
}

/** Cue phases that count as "loaded" for segment readiness (preloaded ahead, on-air, or played-still-loaded). */
const LOADED_CUE_PHASES = new Set<CueItemPhase>(['preloaded', 'on_air', 'played']);

/**
 * Whether every non-skipped cue item belonging to a segment is loaded (preloaded / on-air / played),
 * so the segment row can indicate readiness (tint the play icon success/green). Returns false
 * when the segment has no relevant (non-skipped) items or any relevant item is not yet loaded.
 */
export function isLiveSegmentFullyPreloaded(
  cueState: FlowCueStateV1 | undefined,
  segmentKind: FlowControlSegmentKind | undefined,
  resolvedSlotId: string | undefined,
  skippedItemIds?: readonly string[],
): boolean {
  if (!cueState?.items || !segmentKind) {
    return false;
  }
  // Operator skips live in `stagedSelection.skippedItemIds` (not always reflected as a `skipped`
  // phase), so an unskip must include the item again and a skip must drop it from the aggregate.
  const skippedSet = new Set(skippedItemIds ?? []);
  // scope/slot live in the item KEY (`itemId@scope:slotId`); the entry value doesn't always carry
  // them, so resolve from the key and fall back to the entry fields.
  const relevant = Object.entries(cueState.items).filter(([key, item]) => {
    const parsed = parseCueControlItemKey(key);
    const scope = item.scope ?? parsed.scope;
    const slotId = item.slotId ?? parsed.slotId;
    const itemId = item.itemId ?? parsed.itemId;
    if (!cueScopeMatchesSegmentKind(scope, segmentKind)) {
      return false;
    }
    if (resolvedSlotId && !slotIdsMatch(slotId, resolvedSlotId)) {
      return false;
    }
    if (item.phase === 'skipped' || (itemId != null && skippedSet.has(itemId))) {
      return false;
    }
    return true;
  });
  if (relevant.length === 0) {
    return false;
  }
  return relevant.every(([, item]) => LOADED_CUE_PHASES.has(item.phase));
}
